import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";
import { useTagStore } from "@store/tagStore";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";

interface TagFilterBarProps {
  scope: string;
  selectedTagIds: string[];
  onToggle: (tagId: string) => void;
}

export default function TagFilterBar({ scope, selectedTagIds, onToggle }: TagFilterBarProps) {
  const tags = useTagStore((s) => s.tags)
    .filter((t) => t.scope === scope && t.active)
    .sort((a, b) => a.name.localeCompare(b.name));
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen(!isOpen);
  };

  const selectedTags = tags.filter((t) => selectedTagIds.includes(t.id));
  const hasFilter = selectedTagIds.length > 0;

  const dropdown = isOpen ? createPortal(
    <div
      ref={dropdownRef}
      className="bg-white shadow-lg rounded-xl border flex flex-col"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex: 9999,
        width: 240,
        maxHeight: 260,
      }}
    >
      <div className="px-3 py-2 border-b flex items-center justify-between">
        <span className="text-h4 text-neutral-400">Filtrer par tag</span>
        {hasFilter && (
          <button
            type="button"
            onClick={() => selectedTagIds.forEach(onToggle)}
            className="text-h5 text-neutral-400 hover:text-neutral-600"
          >
            Effacer
          </button>
        )}
      </div>
      <div className="overflow-y-auto p-2 flex flex-row flex-wrap gap-1.5">
        {tags.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id);
          return (
            <span
              key={tag.id}
              className="text-h4 whitespace-nowrap cursor-pointer select-none"
              onClick={() => onToggle(tag.id)}
              style={{
                background: isSelected ? tag.color : "transparent",
                color: isSelected ? "#FFFFFF" : tag.color,
                border: `1.5px solid ${tag.color}`,
                borderRadius: 30,
                padding: "2px 10px",
                lineHeight: "16px",
                opacity: isSelected ? 1 : 0.7,
              }}
            >
              {tag.name}
            </span>
          );
        })}
      </div>
    </div>,
    document.body,
  ) : null;

  if (tags.length === 0) return null;

  return (
    <div className="flex flex-row items-center gap-2">
      <div ref={triggerRef}>
        <SecondaryButton
          onClick={handleOpen}
          startIcon={<Icon1 name="filters" size={14} color={hasFilter ? "#2563EB" : "#A1A1A1"} />}
          style={hasFilter ? { borderColor: "#93C5FD", backgroundColor: "#EFF6FF", color: "#2563EB" } : undefined}
        >
          <>
            Tags
            {hasFilter && (
              <span className="bg-blue-500 text-white rounded-full text-h5 w-4 h-4 flex items-center justify-center ml-1">
                {selectedTagIds.length}
              </span>
            )}
          </>
        </SecondaryButton>
      </div>
      {hasFilter && selectedTags.map((tag) => (
        <span
          key={tag.id}
          className="text-h4 whitespace-nowrap flex items-center gap-1 cursor-pointer"
          onClick={() => onToggle(tag.id)}
          style={{
            background: tag.color,
            color: "#FFFFFF",
            borderRadius: 30,
            padding: "2px 10px",
            lineHeight: "16px",
          }}
        >
          {tag.name}
          <span className="opacity-70">&times;</span>
        </span>
      ))}
      {dropdown}
    </div>
  );
}
